import { useState, useEffect } from 'react';
import { Box, Typography, Card, CardContent, Grid, ToggleButtonGroup, ToggleButton, alpha, Divider, Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { 
  ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, 
  Legend, ResponsiveContainer, Area
} from 'recharts';
import { Savings, ElectricBolt, CalendarMonth, ShowChart } from '@mui/icons-material';

import { getUsageForecast } from '../../services/business';
import { UsageForecastData } from '../../services/business';

// Define the forecast period type
type ForecastPeriod = '3-months' | '1-year';

// Define props interface
interface UsageForecastProps {
  businessId: string;
}

const UsageForecast = ({ businessId }: UsageForecastProps) => {
  const theme = useTheme();
  const [forecastPeriod, setForecastPeriod] = useState<ForecastPeriod>('3-months');
  const [forecastData, setForecastData] = useState<UsageForecastData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  // Fetch the forecast data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getUsageForecast(businessId);
        setForecastData(data);
      } catch (err: any) {
        console.error('Error fetching forecast data:', err);
        setError(`Failed to load forecast data: ${err?.message || 'Unknown error'}`);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [businessId]);
  
  // Handle forecast period change
  const handleForecastPeriodChange = (_event: React.MouseEvent<HTMLElement>, newPeriod: ForecastPeriod | null) => {
    if (newPeriod !== null) {
      setForecastPeriod(newPeriod);
    }
  };
  
  // Get the current forecast dataset based on the selected forecast period
  const getCurrentForecastData = () => {
    if (!forecastData) return { actual: [], forecast: [] };
    
    switch (forecastPeriod) {
      case '3-months':
        return forecastData.forecast_3_months;
      case '1-year':
        return forecastData.forecast_1_year;
      default:
        return forecastData.forecast_3_months;
    }
  };
  
  // Format date for x-axis
  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('default', { month: 'short', year: 'numeric' });
    } catch (err) {
      return dateStr;
    }
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Typography>Loading forecast data...</Typography>
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error" variant="body1">{error}</Typography>
      </Box>
    );
  }
  
  const forecastChartData = getCurrentForecastData();
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Energy Usage Forecasting
        </Typography>
        
        <ToggleButtonGroup
          value={forecastPeriod}
          exclusive
          onChange={handleForecastPeriodChange}
          size="small"
          sx={{ 
            '& .MuiToggleButton-root': { 
              px: 2, 
              fontWeight: 600,
              fontSize: '0.8rem'
            }
          }}
        >
          <ToggleButton value="3-months" aria-label="3 month forecast">
            3 MONTHS
          </ToggleButton>
          <ToggleButton value="1-year" aria-label="1 year forecast">
            1 YEAR
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card 
            elevation={0} 
            sx={{ 
              borderRadius: 3,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
              mb: 3,
              transition: theme.transitions.create(['box-shadow', 'transform'], {
                duration: theme.transitions.duration.standard
              }),
              '&:hover': {
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
                transform: 'translateY(-4px)',
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Usage Forecasting
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Comprehensive view of actual vs predicted usage with advanced visualization options
              </Typography>
              
              <Box sx={{ height: 400, mb: 2 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={processChartData(forecastChartData)}
                    margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 12 }}
                      angle={-45}
                      textAnchor="end"
                      height={70}
                      tickFormatter={formatDate}
                    />
                    <YAxis 
                      label={{ 
                        value: 'Energy Usage (kWh)', 
                        angle: -90, 
                        position: 'insideLeft',
                        style: { textAnchor: 'middle' } 
                      }}
                      tickFormatter={(value) => value.toLocaleString()}
                    />
                    <ChartTooltip 
                      formatter={(value: any, name: string) => {
                        if (value === null) return ['N/A', ''];
                        
                        // Format the value
                        const formattedValue = `${Number(value).toLocaleString()} kWh`;
                        
                        // Update label based on data series
                        if (name === 'actualKwh') {
                          return [formattedValue, 'Actual Usage'];
                        } else if (name === 'forecastKwh') {
                          return [formattedValue, 'Forecasted Usage'];
                        }
                        
                        return [formattedValue, name];
                      }}
                      labelFormatter={(label) => {
                        // Find the corresponding data point
                        const dataPoint = processChartData(forecastChartData).find(item => item.date === label);
                        const date = new Date(label);
                        const monthYear = date.toLocaleDateString('default', { month: 'long', year: 'numeric' });
                        
                        if (dataPoint?.dataType === 'transition') {
                          return `${monthYear} - Partial Actual & Forecast`;
                        }
                        
                        return `Month: ${monthYear}`;
                      }}
                      contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}` }}
                    />
                    <Legend 
                      payload={[
                        { value: 'Actual Usage', type: 'line' as const, color: theme.palette.info.main },
                        { value: 'Forecast Usage', type: 'line' as const, color: theme.palette.success.main }
                      ]}
                      wrapperStyle={{ paddingTop: 20 }}
                    />
                    
                    {/* Create combined dataset that clearly shows overlap */}
                    <defs>
                      <linearGradient id="colorActual" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={theme.palette.info.main} stopOpacity={0.8}/>
                        <stop offset="95%" stopColor={theme.palette.info.main} stopOpacity={0.1}/>
                      </linearGradient>
                      <linearGradient id="colorForecast" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={theme.palette.success.main} stopOpacity={0.8}/>
                        <stop offset="95%" stopColor={theme.palette.success.main} stopOpacity={0.1}/>
                      </linearGradient>
                    </defs>
                    
                    {/* First render the actual data as a solid filled area */}
                    <Area 
                      type="monotone" 
                      dataKey="actualKwh"
                      name="Actual Usage"
                      fill="url(#colorActual)"
                      stroke={theme.palette.info.main}
                      fillOpacity={1}
                      strokeWidth={2}
                      connectNulls
                      isAnimationActive={false}
                    />
                    
                    {/* Then render forecast data on top with transparency */}
                    <Area 
                      type="monotone" 
                      dataKey="forecastKwh"
                      name="Forecast Usage"
                      fill="url(#colorForecast)"
                      stroke={theme.palette.success.main}
                      strokeWidth={2.5}
                      fillOpacity={0.7}
                      connectNulls
                      isAnimationActive={false}
                      dot={{ r: 4, fill: theme.palette.success.main }}
                      activeDot={{ r: 6, stroke: theme.palette.success.dark, strokeWidth: 1 }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </Box>
              
              <Box sx={{ p: 2, bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: 2 }}>
                <Typography variant="body2" align="center">
                  <strong>Time Series Legend:</strong> Blue area shows historical usage, green area shows forecasted usage.
                  {forecastPeriod === '3-months' && ' Showing historical data with overlapping 3 months forecast.'}
                  {forecastPeriod === '1-year' && ' Showing historical data with overlapping 12 months forecast.'}
                </Typography>
              </Box>
              
              <Box sx={{ mt: 3, p: 3, bgcolor: alpha(theme.palette.success.main, 0.05), borderRadius: 2, border: `1px solid ${alpha(theme.palette.success.main, 0.2)}` }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      Current Month Status
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {forecastData?.currentMonth.name}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ position: 'relative', pt: 1, pb: 2 }}>
                      <Typography variant="body2" align="center" gutterBottom>
                        Actual usage (so far)
                      </Typography>
                      <Typography variant="h5" align="center" color="info.main" fontWeight={600}>
                        {forecastData?.currentMonth?.actualKwh?.toLocaleString() || '0'} kWh
                      </Typography>
                      <Typography variant="body2" align="center" color="text.secondary" sx={{ mt: 0.5 }}>
                        {((forecastData?.currentMonth?.actualKwh || 0) / (forecastData?.currentMonth?.forecastKwh || 1) * 100).toFixed(1)}% of forecast
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ position: 'relative', pt: 1, pb: 2 }}>
                      <Typography variant="body2" align="center" gutterBottom>
                        Forecast (full month)
                      </Typography>
                      <Typography variant="h5" align="center" color="success.main" fontWeight={600}>
                        {forecastData?.currentMonth.forecastKwh?.toLocaleString() || 'N/A'} kWh
                      </Typography>
                      <Typography variant="body2" align="center" color="text.secondary" sx={{ mt: 0.5 }}>
                        Based on historical patterns
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ position: 'relative', height: 10, bgcolor: alpha(theme.palette.grey[300], 0.5), borderRadius: 5, overflow: 'hidden' }}>
                      <Box 
                        sx={{ 
                          position: 'absolute', 
                          left: 0, 
                          top: 0, 
                          height: '100%', 
                          width: `${Math.min(((forecastData?.currentMonth?.actualKwh || 0) / (forecastData?.currentMonth?.forecastKwh || 1) * 100), 100)}%`,
                          bgcolor: theme.palette.info.main,
                          borderRadius: 5
                        }} 
                      />
                    </Box>
                    <Typography variant="caption" align="center" display="block" sx={{ mt: 1 }}>
                      Progress bar shows current usage relative to forecast
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card 
            elevation={0} 
            sx={{ 
              borderRadius: 3,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
              height: '100%',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center' }}>
                <Savings sx={{ color: theme.palette.success.main, mr: 1.5 }} />
                Efficiency Opportunities
              </Typography>
              
              <Typography variant="body2" paragraph>
                We've identified the following opportunities to reduce your energy usage:
              </Typography>
              
              <Box>
                <Box sx={{ mb: 2, pb: 2, borderBottom: `1px dashed ${alpha(theme.palette.grey[500], 0.3)}` }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5, display: 'flex', justifyContent: 'space-between' }}>
                    <span>Reducing peak demand</span>
                    <Chip 
                      label="Save up to 15%" 
                      size="small" 
                      sx={{ 
                        bgcolor: alpha(theme.palette.success.main, 0.1),
                        color: theme.palette.success.main,
                        fontWeight: 600,
                      }} 
                    />
                  </Typography>
                  <Typography variant="body2">
                    Shift non-essential operations outside peak hours (1-4PM) to reduce peak demand charges.
                  </Typography>
                </Box>
                
                <Box sx={{ mb: 2, pb: 2, borderBottom: `1px dashed ${alpha(theme.palette.grey[500], 0.3)}` }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5, display: 'flex', justifyContent: 'space-between' }}>
                    <span>Optimize HVAC settings</span>
                    <Chip 
                      label="Save up to 10%" 
                      size="small" 
                      sx={{ 
                        bgcolor: alpha(theme.palette.success.main, 0.1),
                        color: theme.palette.success.main,
                        fontWeight: 600,
                      }} 
                    />
                  </Typography>
                  <Typography variant="body2">
                    Adjusting temperature setpoints by 2-3 degrees during non-critical hours.
                  </Typography>
                </Box>
                
                <Box sx={{ mb: 2, pb: 2, borderBottom: `1px dashed ${alpha(theme.palette.grey[500], 0.3)}` }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5, display: 'flex', justifyContent: 'space-between' }}>
                    <span>Lighting controls</span>
                    <Chip 
                      label="Save up to 8%" 
                      size="small" 
                      sx={{ 
                        bgcolor: alpha(theme.palette.success.main, 0.1),
                        color: theme.palette.success.main,
                        fontWeight: 600,
                      }} 
                    />
                  </Typography>
                  <Typography variant="body2">
                    Install motion sensors and timers to reduce lighting usage in low-traffic areas.
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5, display: 'flex', justifyContent: 'space-between' }}>
                    <span>Equipment maintenance</span>
                    <Chip 
                      label="Save up to 5%" 
                      size="small" 
                      sx={{ 
                        bgcolor: alpha(theme.palette.success.main, 0.1),
                        color: theme.palette.success.main,
                        fontWeight: 600,
                      }} 
                    />
                  </Typography>
                  <Typography variant="body2">
                    Regular maintenance of HVAC and other equipment can improve efficiency and reduce costs.
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card 
            elevation={0} 
            sx={{ 
              borderRadius: 3,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
              height: '100%',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center' }}>
                <ShowChart sx={{ color: theme.palette.primary.main, mr: 1.5 }} />
                Forecast Details
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Usage Patterns
                </Typography>
                <Typography variant="body2" paragraph>
                  Our forecast is based on your historical usage patterns and expected seasonal variations. Notable patterns include:
                </Typography>
                
                <Box component="ul" sx={{ pl: 2 }}>
                  <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                    Highest usage occurs during summer months (June-August)
                  </Typography>
                  <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                    Weekend usage is typically 35% lower than weekday usage
                  </Typography>
                  <Typography component="li" variant="body2">
                    Non-operational hours account for 22% of total energy consumption
                  </Typography>
                </Box>
              </Box>
              
              <Divider sx={{ mb: 3 }} />
              
              <Box>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Forecast Period Coverage
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Box sx={{ 
                      p: 1.5, 
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                      borderRadius: 2,
                      textAlign: 'center'
                    }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Weekly
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {forecastData?.weekly.forecastKwh.toLocaleString()} kWh
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box sx={{ 
                      p: 1.5, 
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                      borderRadius: 2,
                      textAlign: 'center'
                    }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Monthly
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {forecastData?.monthly.forecastKwh.toLocaleString()} kWh
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box sx={{ 
                      p: 1.5, 
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                      borderRadius: 2,
                      textAlign: 'center'
                    }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Yearly
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {forecastData?.yearly.forecastKwh.toLocaleString()} kWh
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

// Add this helper function to process chart data and ensure smooth overlap
const processChartData = (chartData: { actual: any[], forecast: any[] }) => {
  const { actual, forecast } = chartData;
  
  if (actual.length === 0 || forecast.length === 0) {
    return [...actual.map(item => ({
      date: item.date,
      actualKwh: item.kwh,
      forecastKwh: null,
      dataType: 'actual'
    })), ...forecast.map(item => ({
      date: item.date,
      actualKwh: null,
      forecastKwh: item.kwh,
      dataType: 'forecast'
    }))].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }
  
  // Create a map for quick lookup
  const dataMap = new Map();
  
  // Get last actual data point for transition
  const lastActual = actual[actual.length - 1];
  const lastActualDate = new Date(lastActual.date);
  const lastActualValue = lastActual.kwh;
  
  // Get first forecast data point
  const firstForecast = forecast[0];
  const firstForecastValue = firstForecast.kwh;
  
  // Find the data point for February 2025
  const febData = actual.find(item => {
    const date = new Date(item.date);
    return date.getFullYear() === 2025 && date.getMonth() === 1; // February is month 1 (0-indexed)
  });
  
  // Find the data point for March 2025
  const marData = actual.find(item => {
    const date = new Date(item.date);
    return date.getFullYear() === 2025 && date.getMonth() === 2; // March is month 2 (0-indexed)
  });
  
  // Set February 2025 as the start of the forecast overlay
  const transitionStartDate = new Date(2025, 1, 1); // Feb 2025
  
  // Process actual data first
  actual.forEach(item => {
    const itemDate = new Date(item.date);
    const month = itemDate.getMonth();
    const year = itemDate.getFullYear();
    
    // Determine data type
    let dataType = 'actual';
    
    // February and March 2025 are special - we want to overlay forecast
    const isFeb2025 = year === 2025 && month === 1;
    const isMar2025 = year === 2025 && month === 2;
    const isTransitionMonth = isFeb2025 || isMar2025;
    
    let forecastValue = null;
    
    if (isTransitionMonth) {
      dataType = 'transition';
      
      // For February, start the forecast line directly at the actual value
      if (isFeb2025) {
        // Start forecast exactly at the actual value
        forecastValue = item.kwh;
      }
      // For March, start transitioning to the true forecast value
      else if (isMar2025) {
        // We'll have the forecast value gradually transition to the true forecast value
        // by the end of March
        const dayOfMonth = itemDate.getDate();
        const marchTotalDays = 31;
        
        // Use a cubic or quadratic function for smoother transition
        // This creates an accelerating curve rather than linear
        const transitionProgress = Math.pow(dayOfMonth / marchTotalDays, 2);
        
        // Start with Feb value, transition to April value (first true forecast)
        const febValue = febData?.kwh || lastActualValue;
        
        // Calculate target value - for a smooth drop from Feb to Mar to April's forecast
        forecastValue = febValue - (transitionProgress * (febValue - firstForecastValue));
      }
    }
    
    dataMap.set(item.date, { 
      date: item.date, 
      actualKwh: item.kwh,
      forecastKwh: isTransitionMonth ? forecastValue : null,
      dataType
    });
  });
  
  // Process forecast data
  forecast.forEach(item => {
    const itemDate = new Date(item.date);
    
    if (dataMap.has(item.date)) {
      // For overlap points, use both actual and forecast values
      const existingItem = dataMap.get(item.date);
      
      // For February/March (transition months), we want our calculated value
      // rather than the raw forecast value
      const month = itemDate.getMonth();
      const year = itemDate.getFullYear();
      const isTransitionMonth = (year === 2025 && (month === 1 || month === 2));
      
      if (!isTransitionMonth) {
        existingItem.forecastKwh = item.kwh;
      }
      
      // Set data type
      if (year > 2025 || (year === 2025 && month >= 1)) { // From Feb 2025 onward
        existingItem.dataType = (month === 1 || month === 2) ? 'transition' : 'forecast';
      }
      
      dataMap.set(item.date, existingItem);
    } else {
      // For forecast-only points
      dataMap.set(item.date, { 
        date: item.date, 
        actualKwh: null,
        forecastKwh: item.kwh,
        dataType: 'forecast'
      });
    }
  });
  
  // Convert map back to array and sort by date
  return Array.from(dataMap.values())
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
};

export default UsageForecast; 